import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  CircularProgress,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useInformeFinal, useInformeParcial } from '@/features/informes/hooks/useInformes';
import { useNotify } from '@/hooks/useNotify';
import { abrirInformePDF } from '@/lib/abrirInformePDF';

import type { Protocolo } from '../types';
import { EliminarProtocoloDialog } from './EliminarProtocoloDialog';

interface ProtocoloKebabMenuProps {
  protocolo: Protocolo;
  canManage: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

export function ProtocoloKebabMenu({
  protocolo,
  canManage,
  anchorEl,
  onClose,
}: ProtocoloKebabMenuProps): JSX.Element {
  const navigate = useNavigate();
  const notify = useNotify();
  const [deleteOpen, setDeleteOpen] = useState(false);

  // enabled: false — solo se disparan con refetch() al hacer click
  const informeParcialQuery = useInformeParcial(protocolo.uid_protocolo);
  const informeFinalQuery = useInformeFinal(protocolo.uid_protocolo);

  const isClosed = protocolo.informe_final_emitido;
  const downloadQuery = isClosed ? informeFinalQuery : informeParcialQuery;

  function handleVerDetalle(): void {
    onClose();
    navigate(`/protocolos/${protocolo.uid_protocolo}`);
  }

  async function handleDescargar(): Promise<void> {
    const ok = await abrirInformePDF(
      async () => {
        const result = await downloadQuery.refetch();
        return result.data?.presigned_url;
      },
      notify.error,
      'No se pudo descargar el informe. Intentá de nuevo.',
    );
    if (ok) onClose();
  }

  function handleEliminar(): void {
    onClose();
    setDeleteOpen(true);
  }

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleVerDetalle}>
          <ListItemIcon>
            <VisibilityOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Ver detalle
        </MenuItem>

        {canManage && (
          <MenuItem onClick={() => void handleDescargar()} disabled={downloadQuery.isFetching}>
            <ListItemIcon>
              {downloadQuery.isFetching ? (
                <CircularProgress size={16} />
              ) : (
                <DownloadOutlinedIcon fontSize="small" />
              )}
            </ListItemIcon>
            {downloadQuery.isFetching ? 'Descargando...' : 'Descargar informe'}
          </MenuItem>
        )}

        {canManage && (
          <>
            <Divider />
            <Tooltip title={isClosed ? 'No se puede eliminar un protocolo cerrado.' : ''}>
              <span>
                <MenuItem
                  onClick={handleEliminar}
                  disabled={isClosed}
                  sx={{ color: 'error.main' }}
                >
                  <ListItemIcon>
                    <DeleteOutlinedIcon
                      fontSize="small"
                      color={isClosed ? 'disabled' : 'error'}
                    />
                  </ListItemIcon>
                  Eliminar
                </MenuItem>
              </span>
            </Tooltip>
          </>
        )}
      </Menu>

      <EliminarProtocoloDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        protocolo={protocolo}
      />
    </>
  );
}
